import _get from 'lodash/get'
import { z } from 'zod'

import type { Language } from '@nxt-insurance/constants'
import { SupportedMarketKey } from '@nxt-insurance/constants'
import { IntlFormatter } from '@nxt-insurance/formatting'

import { buildPaymentStep } from '@/app/utils/flows/paymentStep'
import { getQuotedZipCodePath, withQuotedZipCodeCheck } from '@/app/utils/flows/quotedZipCode'
import {
  buildAlreadyPurchasedStep,
  buildFailureStep,
  buildProgressBarSections,
  buildSuccessStep,
} from '@/app/utils/flows/sharedFlowSteps'
import { getApiService } from '@/services/api'
import { flowConfigsDe } from '@flows/configs'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

const PRODUCT_KEY = 'liability_v1_getsafe_de' as const
const I18N_NAMESPACE = 'sharedFlows_liabilityNxt' as const
const LIABILITY_FIRST_STEP_NAME = 'liability_instant' as const

const getConfigParam = (flowValues: FlowValues, param: string): unknown =>
  _get(flowValues, ['products', PRODUCT_KEY, 'configuration_parameters', param])

const QUOTED_ZIP_CODE_PATH = getQuotedZipCodePath(PRODUCT_KEY)

export const getLiabilityFlowConfig = (
  language: Language,
  req?: Parameters<typeof getApiService>[0]['req'],
  res?: Parameters<typeof getApiService>[0]['res']
) => {
  const formatter = new IntlFormatter({
    getCurrentLanguage: () => language,
    getCurrentMarket: () => SupportedMarketKey.De,
  })
  const apiService = getApiService({ language, req, res })
  const base = flowConfigsDe.liabilityNxt({
    clientHelpers: {
      apiService,
      getMarket: () => SupportedMarketKey.De,
      formatter,
    },
    market: SupportedMarketKey.De,
  })

  const baseSteps = base.getSteps()
  const baseOptions = base.getOptions()

  const { nextStep: _omitInstantNext, ...instantWithoutNext } = baseSteps.liability_instant
  const baseInstantSubmit = instantWithoutNext.onSubmit
  const liability_instant = {
    ...instantWithoutNext,
    onSubmit: {
      ...baseInstantSubmit,
      outputs: [...baseInstantSubmit.outputs, QUOTED_ZIP_CODE_PATH],
      callback: async (flowValues: FlowValues) => {
        const result = await baseInstantSubmit.callback(flowValues)
        if (result && 'error' in result) {
          return result
        }
        return {
          formValues: { ...result?.formValues, [QUOTED_ZIP_CODE_PATH]: flowValues.zip_code },
        }
      },
    },
    buttonLabel: {
      type: 'translatableText',
      translationKey:
        'sharedFlows_common:customComponents.liabilityInstantQuoteQuestions.footerButton.label',
    },
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        const numberOfClaimsValue = getConfigParam(flowValues, 'number_of_claims_in_last_5y')
        if (numberOfClaimsValue === 3) {
          return 'failure'
        }
        return 'liability_quote'
      },
      valueToStepName: {
        liability_quote: 'liability_quote',
        failure: 'failure',
      },
    },
  } as const satisfies AnyStepConfiguration

  const { nextStep: _omitQuoteNext, ...baseQuoteStep } = baseSteps.liability_quote
  const liability_quote = {
    ...baseQuoteStep,
    showOrderSummary: true,
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        const previouslyOwnedValue = getConfigParam(flowValues, 'previous_insurance_in_last_5y')
        return previouslyOwnedValue ? 'liability_cancelled' : 'liability_personal_details'
      },
      valueToStepName: {
        liability_cancelled: 'liability_cancelled',
        liability_personal_details: 'liability_personal_details',
      },
    },
    schema: baseSteps.liability_quote.schema.and(
      z.object({ startDates: z.object({ [PRODUCT_KEY]: z.record(z.string()) }) })
    ),
  } as const satisfies AnyStepConfiguration

  const { nextStepConfig: _omitCancelledNextConfig, ...baseCancelled } =
    baseSteps.liability_cancelled
  const liability_cancelled = {
    ...baseCancelled,
    showOrderSummary: true,
    subFlow: SubFlow.Common,
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        return getConfigParam(flowValues, 'cancelled_by_previous_insurer')
          ? 'failure'
          : 'liability_personal_details'
      },
      valueToStepName: {
        liability_personal_details: 'liability_personal_details',
        failure: 'failure',
      },
    },
  } as const satisfies AnyStepConfiguration

  const liability_personal_details = withQuotedZipCodeCheck(
    {
      ...baseSteps.liability_personal_details,
      showOrderSummary: true,
      nextStep: 'payment',
    } as const,
    {
      productKey: PRODUCT_KEY,
      translationKey: `${I18N_NAMESPACE}:steps.liability.address.zipMismatch`,
    }
  )

  const payment = buildPaymentStep(I18N_NAMESPACE)

  return {
    getSteps: () => ({
      liability_instant,
      liability_quote,
      liability_cancelled,
      liability_personal_details,
      payment,
      already_purchased: buildAlreadyPurchasedStep(),
      success: buildSuccessStep(),
      failure: buildFailureStep(I18N_NAMESPACE),
    }),
    getOptions: () => ({
      ...baseOptions,
      constants: {
        ...baseOptions.constants,
        FIRST_STEP_NAME: LIABILITY_FIRST_STEP_NAME,
        flowName: 'liability',
        productKey: PRODUCT_KEY,
      },
      progressBarSections: buildProgressBarSections({
        insuranceSubFlow: SubFlow.LiabilityNxt,
        i18nNamespace: I18N_NAMESPACE,
      }),
    }),
  }
}
